import { request } from 'graphql-request';
import useSWRMutation from 'swr/mutation';

import config from '@/core/config/config';
import { CLEAR_SESSION } from '@/graphql/mutations/apps/clearSession';
import { CREATE_HOLD_EVENT } from '@/graphql/mutations/apps/createHoldEvent';
import { CREATE_SESSION } from '@/graphql/mutations/apps/createSession';
import { REMOVE_HOLD_EVENT } from '@/graphql/mutations/apps/removeHoldEvent';
import { UPDATE_SESSION } from '@/graphql/mutations/apps/updateSession';
import { SESSION } from '@/graphql/queries';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { Session } from '@/types';
import { customFetcher, getGqlString } from '@/utils/customFetcher';
import { mutationConfig, swrConfig } from '@/utils/swrConfig';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useGlobalLoading } from './useGlobalLoading';

export const getSessionQueryFetcher = (url: string) => {
  return customFetcher(url, {
    query: getGqlString(SESSION),
  });
};

const createSessionMutationFetcher = (url: string, { arg }: { arg: any }) => {
  return customFetcher(url, {
    query: getGqlString(CREATE_SESSION),
    variables: arg,
  });
};

const updateSessionFetcher = (url: string, { arg }: { arg: any }) => {
  return customFetcher(url, {
    query: getGqlString(UPDATE_SESSION),
    variables: arg,
  });
};

const createHoldEventMutationFetcher = (url: string, { arg }: { arg: any }) => {
  return request(url, CREATE_HOLD_EVENT, arg);
};

const removeHoldEventMutationFetcher = (url: string, { arg }: { arg: any }) => {
  return request(url, REMOVE_HOLD_EVENT, arg);
};

const clearSessionMutationFetcher = (url: string) => {
  return customFetcher(url, {
    query: getGqlString(CLEAR_SESSION),
  });
};

export const useGetSession = () => {
  return useSWRMutation(config.APPS_SERVER, getSessionQueryFetcher, mutationConfig);
};

export const useClearSession = () => {
  return useSWRMutation(config.APPS_SERVER, clearSessionMutationFetcher, mutationConfig);
};

export const useCreateHoldEvent = () => {
  return useSWRMutation(config.APPS_SERVER, createHoldEventMutationFetcher, mutationConfig);
};

export const useCreateSession = () => {
  return useSWRMutation(config.APPS_SERVER, createSessionMutationFetcher, mutationConfig);
};

export const useRemoveHoldEvent = () => {
  return useSWRMutation(config.APPS_SERVER, removeHoldEventMutationFetcher, mutationConfig);
};

export const useUpdateSession = () => {
  return useSWRMutation(config.APPS_SERVER, updateSessionFetcher, mutationConfig);
};

// TODO: move enum, type, util to a separate file
export enum SessionStatusEnum {
  LOADING = 'loading',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ERROR = 'error',
}

export type SessionStatus = SessionStatusEnum;

export const getSessionStatus = (session: Session) => {
  if (session === undefined) {
    return SessionStatusEnum.LOADING;
  }
  if (session === null) {
    return SessionStatusEnum.INACTIVE;
  }
  if (session?.eventId) {
    return SessionStatusEnum.ACTIVE;
  }
  return SessionStatusEnum.ERROR;
};

export const useSession = () => {
  const { session, setSession } = usePrenuvoStore(
    useShallow((state) => ({
      session: state.scan.session,
      setSession: state.scan.methods.setSession,
    })),
  );
  const { trigger: triggerClearSession, isMutating: isClearingSession } = useClearSession();
  const { trigger: triggerCreateSession, isMutating: isCreatingSession } = useCreateSession();
  const { trigger: triggerUpdateSession, isMutating: isUpdatingSession } = useUpdateSession();
  const { trigger: triggerCreateHoldEvent, isMutating: isCreatingHoldEvent } = useCreateHoldEvent();
  const { trigger: triggerRemoveHoldEvent, isMutating: isRemovingHoldEvent } = useRemoveHoldEvent();
  const {
    trigger: triggeruseGetSession,
    data: sessionData,
    isMutating: isSessionLoading,
    error: sessionError,
  } = useGetSession();

  useEffect(() => {
    if (sessionData && sessionData.data) {
      setSession(sessionData.data.getSession);
      return;
    }

    if (sessionError) console.error(sessionError);
  }, [sessionData, sessionError, setSession]);

  const isLoading = useMemo(
    () =>
      isSessionLoading ||
      isCreatingHoldEvent ||
      isRemovingHoldEvent ||
      isCreatingSession ||
      isClearingSession ||
      isUpdatingSession,
    [
      isSessionLoading,
      isCreatingHoldEvent,
      isRemovingHoldEvent,
      isCreatingSession,
      isClearingSession,
      isUpdatingSession,
    ],
  );

  const sessionStatus = useMemo(() => {
    //TODO: session should be its own slice and session status should be managed by the slice
    return getSessionStatus(session);
  }, [session]);

  useGlobalLoading(isLoading);

  return {
    sessionStatus,
    triggerClearSession,
    triggerCreateSession,
    triggerRemoveHoldEvent,
    triggerUpdateSession,
    triggerCreateHoldEvent,
    triggeruseGetSession,
  };
};
